import React, {CSSProperties} from "react";
import {
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  IssueOverrideTag,
  IssueOverrideTagId,
} from "common/types/issue_override_tag";
import EditableTagName from "./editable_tag_name";

interface IssueOverrideTagListProps {
  issueOverrideTags: IssueOverrideTag[];
  onEditTag: (tagId: IssueOverrideTagId, name: string) => Promise<void>;
  onDeleteTag: (tagId: IssueOverrideTagId) => void;
}

const styles: Record<string, CSSProperties> = {
  root: {
    padding: 24,
  },
  paper: {
    marginTop: 16,
  },
  list: {
    width: "100%",
    padding: 0,
  },
  listItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  header: {
    marginBottom: 16,
    fontSize: "1.25rem",
  },
  emptyMessage: {
    padding: 16,
    textAlign: "center",
  },
  projectChips: {
    flex: 1,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    paddingRight: 48,
    padding: 0,
  },
  deleteButtonContainer: {
    right: 8,
  },
  chip: {
    margin: 0,
  },
  noProjectsText: {
    padding: 0,
    margin: 0,
  },
};

const IssueOverrideTagList: React.FC<IssueOverrideTagListProps> = ({
  issueOverrideTags,
  onEditTag,
  onDeleteTag,
}) => {
  return (
    <div style={styles.root}>
      <Typography variant="h4" component="h1" style={styles.header}>
        Issue Override Tags
      </Typography>

      <Paper style={styles.paper}>
        {issueOverrideTags.length === 0 ? (
          <Typography style={styles.emptyMessage}>
            No issue override tags exist. Add new tags to projects and they will
            appear here
          </Typography>
        ) : (
          <List style={styles.list}>
            {issueOverrideTags.map((tag, index) => (
              <div key={tag.id}>
                <ListItem style={styles.listItem}>
                  <EditableTagName tag={tag} onEdit={onEditTag} />
                  <div style={styles.projectChips}>
                    {tag.projects.length ? (
                      tag.projects.map(project => (
                        <Chip
                          key={project.id}
                          label={project.name}
                          size="small"
                          style={styles.chip}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={styles.noProjectsText}
                      >
                        No projects using this tag
                      </Typography>
                    )}
                  </div>
                  <ListItemSecondaryAction style={styles.deleteButtonContainer}>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => onDeleteTag(tag.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < issueOverrideTags.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        )}
      </Paper>
    </div>
  );
};

export default IssueOverrideTagList;
